import { useSplitClient } from '@splitsoftware/splitio-react';
import { useFeatureFlagUserQuery } from '../graphql/types';
import { SplitFlags } from './featureFlags';
import useLocalStorage from './useLocalStorage';

export type FeatureFlags = Record<SplitFlags, boolean>;

export const useFeatureFlags = () => {
  const { client, isReady } = useSplitClient();
  const { data } = useFeatureFlagUserQuery({
    fetchPolicy: 'cache-first',
  });
  const {
    id: userId = '',
    isAdmin = false,
    email = '',
    coachData,
  } = data?.self ?? {};
  const [flagOverrides] = useLocalStorage<Partial<FeatureFlags>>(
    `${userId}-featureFlag-overrides`,
    {},
  );

  const clientIds = coachData?.clients?.edges.map(({ node }) => node.id) ?? [];
  const attributes = {
    userId,
    isAdmin,
    email,
    clientIds,
    isHpc: Boolean(coachData?.hasHpcMembers),
    appName: 'prince-web',
    locationIds: coachData?.locations?.edges.map(({ node }) => node.id) || [],
  };

  const treatments =
    client?.getTreatmentsWithConfig(Object.values(SplitFlags), attributes) ??
    {};
  const normalizedTreatments = {} as FeatureFlags;

  Object.keys(treatments).forEach((id) => {
    normalizedTreatments[id as SplitFlags] = treatments[id].treatment === 'on';
  });

  const flagsWithOverrides = {
    ...normalizedTreatments,
    ...flagOverrides,
  };

  return {
    isReady,
    flags: flagsWithOverrides,
    rawFlags: normalizedTreatments,
    flagOverrides,
  };
};

export const useGetFlagFromEmail = (): ((
  flag: string,
  email: string,
) => Promise<Boolean>) => {
  const { client } = useSplitClient();
  return async (flag, email) =>
    (await client?.getTreatment(flag, {
      email,
    })) === 'on';
};
